import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const HOUR = 60 * 60 * 1000;
const coolingDown = (r) => r.last_reminded_at && Date.now() - new Date(r.last_reminded_at).getTime() < HOUR;

const CompanyRow = ({ c, onRemind, busy }) => {
  const r = c.kvkk_renewal || {};
  return (
    <li className="py-2.5 flex items-center gap-3" data-testid={`kvkk-company-row-${c.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
        <p className="text-xs text-slate-500 truncate">
          {r.uploaded_at ? `Yükledi: ${fmt(r.uploaded_at)}` : `Son hatırlatma: ${fmt(r.last_reminded_at)} · ${r.reminder_count || 0} hatırlatma`}
        </p>
      </div>
      {!r.uploaded_at && (
        <Button size="sm" variant="outline" onClick={() => onRemind([c.id])} disabled={busy || coolingDown(r)} title={coolingDown(r) ? 'Bu firmaya son 1 saat içinde hatırlatma gönderildi' : undefined} data-testid={`kvkk-company-remind-${c.id}`}>
          <BellRing className="h-3.5 w-3.5 mr-1" />Hatırlat
        </Button>
      )}
    </li>
  );
};

export const KvkkVersionCompaniesDialog = ({ path = 'kvkk-template', version, onClose, onChanged }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!version) return;
    axios.get(`${API}/settings/${path}/${version.id}/companies`).then(res => setItems(res.data)).catch(() => setItems([]));
  }, [version]);
  useEffect(() => { setItems(null); load(); }, [load]);

  const remind = async (ids) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/settings/${path}/${version.id}/remind`, { company_ids: ids });
      const { sent, skipped } = res.data;
      if (sent) toast.success(`${sent} firmaya hatırlatma gönderildi${skipped ? `, ${skipped} firma son 1 saatte hatırlatıldığı için atlandı` : ''}`);
      else toast.info(skipped ? 'Seçilen firmalara son 1 saat içinde zaten hatırlatma gönderildi' : 'Gönderilecek firma yok');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hatırlatma gönderilemedi');
    } finally { setBusy(false); }
  };

  const pending = (items || []).filter(c => !c.kvkk_renewal?.uploaded_at);
  const done = (items || []).filter(c => c.kvkk_renewal?.uploaded_at);
  const remindable = pending.filter(c => !coolingDown(c.kvkk_renewal || {}));

  return (
    <Dialog open={!!version} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="kvkk-companies-dialog">
        <DialogHeader>
          <DialogTitle>v{version?.version} Taahhütnamesi · Firma Durumu</DialogTitle>
          <DialogDescription>Yeni taahhütname istenen firmalar. Yüklemeyenlere elle hatırlatma gönderebilirsiniz (firma başına saatte en fazla bir kez).</DialogDescription>
        </DialogHeader>
        {!items ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <Tabs defaultValue="pending">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <TabsList>
                <TabsTrigger value="pending" data-testid="kvkk-companies-tab-pending">Yüklemeyenler ({pending.length})</TabsTrigger>
                <TabsTrigger value="done" data-testid="kvkk-companies-tab-done">Yükleyenler ({done.length})</TabsTrigger>
              </TabsList>
              {remindable.length > 0 && (
                <Button size="sm" onClick={() => remind(remindable.map(c => c.id))} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="kvkk-companies-remind-all">
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <BellRing className="h-3.5 w-3.5 mr-1" />}Tümüne Hatırlat ({remindable.length})
                </Button>
              )}
            </div>
            <TabsContent value="pending">
              {pending.length ? <ul className="divide-y">{pending.map(c => <CompanyRow key={c.id} c={c} onRemind={remind} busy={busy} />)}</ul>
                : <p className="text-sm text-emerald-700 py-3" data-testid="kvkk-companies-all-done">Tüm firmalar yeni taahhütnameyi yükledi.</p>}
            </TabsContent>
            <TabsContent value="done">
              {done.length ? <ul className="divide-y">{done.map(c => <CompanyRow key={c.id} c={c} />)}</ul>
                : <p className="text-sm text-slate-500 py-3">Henüz yükleyen firma yok.</p>}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
